// frontend/src/pages/Admin.jsx

import React, { useEffect, useMemo, useState } from "react";
import api from "../api";

const DEPARTMENTS = ["CS", "IT"];

const PASSWORD_HELP =
  "Password must be at least 8 characters and include 1 lowercase letter, 1 uppercase letter, and 1 number.";

const COURSE_CODE_HELP =
  "Course code must be CS-XXX for CS department or IT-XXX for IT department. Example: CS-401 or IT-301.";

const styles = {
  page: { display: "grid", gap: 16 },
  headerCard: {
    background: "#fff",
    borderRadius: 14,
    border: "1px solid #e5e7eb",
    padding: 16,
  },
  card: {
    background: "#fff",
    borderRadius: 14,
    border: "1px solid #e5e7eb",
    padding: 16,
  },
  titleRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 10,
    flexWrap: "wrap",
  },
  h2: { margin: 0, fontSize: 22 },
  h3: { margin: 0, fontSize: 18 },
  h4: { margin: 0, fontSize: 15, fontWeight: 900 },
  hint: { marginTop: 6, opacity: 0.75, lineHeight: 1.4 },
  grid: { display: "grid", gap: 10 },
  row: { display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" },
  input: {
    width: "100%",
    padding: "10px 12px",
    borderRadius: 10,
    border: "1px solid #d1d5db",
    outline: "none",
    boxSizing: "border-box",
  },
  passwordWrap: {
    position: "relative",
    width: "100%",
  },
  passwordInput: {
    width: "100%",
    padding: "10px 42px 10px 12px",
    borderRadius: 10,
    border: "1px solid #d1d5db",
    outline: "none",
    boxSizing: "border-box",
  },
  eyeBtn: {
    position: "absolute",
    right: 10,
    top: "50%",
    transform: "translateY(-50%)",
    border: "none",
    background: "transparent",
    cursor: "pointer",
    fontSize: 16,
    padding: 4,
  },
  textarea: {
    width: "100%",
    padding: "10px 12px",
    borderRadius: 10,
    border: "1px solid #d1d5db",
    outline: "none",
    minHeight: 90,
    boxSizing: "border-box",
    resize: "vertical",
  },
  select: {
    width: "100%",
    padding: "10px 12px",
    borderRadius: 10,
    border: "1px solid #d1d5db",
    outline: "none",
    background: "#fff",
    boxSizing: "border-box",
  },
  btn: {
    padding: "10px 14px",
    borderRadius: 10,
    border: "1px solid #d1d5db",
    background: "#fff",
    cursor: "pointer",
  },
  btnDanger: {
    padding: "10px 14px",
    borderRadius: 10,
    border: "1px solid #dc2626",
    background: "#dc2626",
    color: "#fff",
    cursor: "pointer",
  },
  btnPrimary: {
    padding: "10px 14px",
    borderRadius: 10,
    border: "1px solid #1d4ed8",
    background: "#2563eb",
    color: "#fff",
    cursor: "pointer",
  },
  chip: {
    display: "inline-block",
    padding: "4px 10px",
    borderRadius: 999,
    border: "1px solid #e5e7eb",
    background: "#f9fafb",
    fontSize: 12,
    opacity: 0.9,
    whiteSpace: "nowrap",
  },
  divider: { height: 1, background: "#e5e7eb", margin: "12px 0" },
  small: { fontSize: 12, opacity: 0.8, lineHeight: 1.4 },
  errorText: {
    fontSize: 12,
    color: "#dc2626",
    lineHeight: 1.4,
  },
  tableWrap: {
    overflowX: "auto",
    border: "1px solid #e5e7eb",
    borderRadius: 12,
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
  },
  th: {
    textAlign: "left",
    padding: 10,
    background: "#f9fafb",
    borderBottom: "1px solid #e5e7eb",
    fontSize: 13,
    color: "#374151",
  },
  td: {
    padding: 10,
    borderBottom: "1px solid #f3f4f6",
    fontSize: 13,
    verticalAlign: "top",
  },
  miniCard: {
    border: "1px solid #e5e7eb",
    borderRadius: 12,
    padding: 12,
    background: "#f9fafb",
  },
};

function safeParseClos(raw) {
  if (!raw) return [];

  try {
    const data = JSON.parse(raw);

    if (!Array.isArray(data)) return [];

    return data
      .map((x) => {
        if (typeof x === "string") {
          return { code: x, description: "" };
        }

        return {
          code: (x?.code || "").toString(),
          description: (x?.description || "").toString(),
        };
      })
      .filter((c) => c.code || c.description);
  } catch {
    return [];
  }
}

function normalizeRoleLabel(role) {
  const r = (role || "").toString().toLowerCase();

  if (r.includes("course") && r.includes("lead")) return "course_lead";
  if (r.includes("instructor")) return "instructor";
  if (r.includes("faculty")) return "faculty";
  if (r.includes("admin")) return "admin";
  if (r.includes("qec")) return "qec";
  if (r.includes("hod")) return "hod";

  return role || "unknown";
}

function formatUserOption(u) {
  const role = normalizeRoleLabel(u?.role);
  const name = u?.full_name || u?.username || "Unknown";
  const username = u?.username ? `@${u.username}` : "";

  return `${name} ${username} (${role})`;
}

function isStrongPassword(password) {
  return /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/.test(password || "");
}

function normalizeCourseCode(code) {
  return (code || "").trim().toUpperCase();
}

function normalizeDepartment(department) {
  return (department || "").trim().toUpperCase();
}

function isValidCourseCodeForDepartment(code, department) {
  const normalizedCode = normalizeCourseCode(code);
  const dept = normalizeDepartment(department);

  if (!DEPARTMENTS.includes(dept)) return false;

  return new RegExp(`^${dept}-\\d{3}$`).test(normalizedCode);
}

function normalizeUsername(username) {
  return (username || "").trim();
}

function normalizeEmail(email) {
  return (email || "").trim().toLowerCase();
}

function getBackendError(err, fallback) {
  const detail = err?.response?.data?.detail;

  if (Array.isArray(detail)) {
    return detail
      .map((item) => item?.msg || item?.message || JSON.stringify(item))
      .join("\n");
  }

  if (typeof detail === "string") return detail;

  return fallback;
}

function formatCourseOption(c) {
  return `${c.course_code || "Course"} — ${c.course_name || ""}`;
}

function formatOfferingOption(o) {
  const course = o.course_code
    ? `${o.course_code} — ${o.course_name || ""}`
    : o.title || "Offering";

  return `${course} | ${o.semester || "-"} ${o.session || "-"} | ${
    o.batch || "-"
  } ${o.section || "-"} | ${o.instructor_name || "No instructor"}`;
}

function parseStudentsText(text) {
  const raw = (text || "").trim();

  if (!raw) return [];

  try {
    const parsed = JSON.parse(raw);

    if (Array.isArray(parsed)) {
      return parsed
        .map((x) => ({
          roll_no: String(x.roll_no || x.reg_no || x.roll || "").trim(),
          student_name: String(x.student_name || x.name || "").trim(),
          email: x.email ? String(x.email).trim() : null,
        }))
        .filter((x) => x.roll_no && x.student_name);
    }
  } catch {
    // Continue to CSV/plain parsing
  }

  return raw
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const parts = line.split(",").map((x) => x.trim());

      return {
        roll_no: parts[0] || "",
        student_name: parts[1] || "",
        email: parts[2] || null,
      };
    })
    .filter((x) => x.roll_no && x.student_name);
}

export default function Admin({ user }) {
  const isAdmin = useMemo(
    () => (user?.role || "").toLowerCase().includes("admin"),
    [user]
  );

  const [courses, setCourses] = useState([]);
  const [instructors, setInstructors] = useState([]);
  const [courseLeads, setCourseLeads] = useState([]);
  const [selectedCourseId, setSelectedCourseId] = useState("");

  const [offerings, setOfferings] = useState([]);
  const [selectedOfferingId, setSelectedOfferingId] = useState("");
  const [offeringStudents, setOfferingStudents] = useState([]);
  const [studentsText, setStudentsText] = useState(
    "221001, Ali Khan, ali@example.com\n221002, Sara Ahmed, sara@example.com"
  );

  const [offeringForm, setOfferingForm] = useState({
    course_id: "",
    instructor_id: "",
    course_lead_id: "",
    semester: "Spring",
    session: "2026",
    batch: "BSCS 8",
    section: "B",
    department: "CS",
    title: "",
  });

  const [showCreatePassword, setShowCreatePassword] = useState(false);
  const [showEditPassword, setShowEditPassword] = useState(false);

  const [form, setForm] = useState({
    course_code: "CS-",
    course_name: "",
    semester: "Fall",
    year: "2026",
    department: "CS",
  });

  const [clos, setClos] = useState([{ code: "CLO1", description: "" }]);

  const [assign, setAssign] = useState({
    instructorId: "",
    courseLeadId: "",
  });

  const [newUser, setNewUser] = useState({
    full_name: "",
    username: "",
    email: "",
    department: "CS",
    role: "instructor",
    password: "Teacher123",
  });

  const [allTeachers, setAllTeachers] = useState([]);

  const [editUserId, setEditUserId] = useState("");
  const [editForm, setEditForm] = useState({
    full_name: "",
    username: "",
    email: "",
    department: "CS",
    password: "",
  });

  const [editCourseId, setEditCourseId] = useState("");
  const [editCourseForm, setEditCourseForm] = useState({
    course_code: "",
    course_name: "",
    semester: "",
    year: "",
    department: "CS",
    instructor: "",
    clos: "[]",
  });

  const [savingEdit, setSavingEdit] = useState(false);
  const [savingCourseEdit, setSavingCourseEdit] = useState(false);
  const [savingOffering, setSavingOffering] = useState(false);
  const [savingStudents, setSavingStudents] = useState(false);
  const [loading, setLoading] = useState(false);
  const [offeringLoading, setOfferingLoading] = useState(false);
  const [studentsLoading, setStudentsLoading] = useState(false);
  const [closLoading, setClosLoading] = useState(false);

  const loadOfferings = async () => {
    setOfferingLoading(true);

    try {
      const res = await api.get("/course-offerings/");
      const list = res.data || [];

      setOfferings(list);

      if (!selectedOfferingId && list.length) {
        setSelectedOfferingId(list[0].id);
      }
    } finally {
      setOfferingLoading(false);
    }
  };

  const loadOfferingStudents = async (offeringId) => {
    if (!offeringId) {
      setOfferingStudents([]);
      return;
    }

    setStudentsLoading(true);
    setOfferingStudents([]);

    try {
      const res = await api.get(`/course-offerings/${offeringId}/students`);
      setOfferingStudents(res.data || []);
    } finally {
      setStudentsLoading(false);
    }
  };

  const load = async () => {
    setLoading(true);

    try {
      const [cRes, iRes, lRes, oRes] = await Promise.all([
        api.get("/courses"),
        api.get("/admin/users?role=instructor"),
        api.get("/admin/users?role=course_lead"),
        api.get("/course-offerings/"),
      ]);

      const cList = cRes.data || [];
      const instr = iRes.data || [];
      const leads = lRes.data || [];
      const offeringList = oRes.data || [];

      setCourses(cList);
      setInstructors(instr);
      setCourseLeads(leads);
      setAllTeachers([...(instr || []), ...(leads || [])]);
      setOfferings(offeringList);

      if (!selectedCourseId && cList.length) {
        setSelectedCourseId(cList[0].id);
      }

      if (!offeringForm.course_id && cList.length) {
        setOfferingForm((prev) => ({
          ...prev,
          course_id: cList[0].id,
          department: normalizeDepartment(cList[0].department) || "CS",
        }));
      }

      if (!selectedOfferingId && offeringList.length) {
        setSelectedOfferingId(offeringList[0].id);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isAdmin) return;

    load().catch(console.error);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  useEffect(() => {
    if (!selectedCourseId) return;

    setClosLoading(true);
    setClos([{ code: "CLO1", description: "" }]);

    const timer = setTimeout(() => {
      const course = courses.find((c) => c.id === selectedCourseId);
      const parsed = safeParseClos(course?.clos);

      if (parsed.length) {
        setClos(parsed);
      } else {
        setClos([{ code: "CLO1", description: "" }]);
      }

      setClosLoading(false);
    }, 100);

    return () => clearTimeout(timer);
  }, [selectedCourseId, courses]);

  useEffect(() => {
    if (!selectedOfferingId) return;

    loadOfferingStudents(selectedOfferingId).catch(console.error);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOfferingId]);

  if (!isAdmin) {
    return (
      <div style={styles.card}>
        <h2 style={styles.h2}>Admin</h2>
        <p style={styles.hint}>You do not have access to this page.</p>
      </div>
    );
  }

  const createCourse = async (e) => {
    e.preventDefault();

    const courseCode = normalizeCourseCode(form.course_code);
    const department = normalizeDepartment(form.department);

    if (!isValidCourseCodeForDepartment(courseCode, department)) {
      alert(COURSE_CODE_HELP);
      return;
    }

    try {
      await api.post("/admin/courses", {
        ...form,
        course_code: courseCode,
        department,
        instructor: "",
        clos: "[]",
      });

      alert("Course created successfully");

      setForm({
        course_code: "CS-",
        course_name: "",
        semester: "Fall",
        year: "2026",
        department: "CS",
      });

      await load();
    } catch (err) {
      console.error(err);
      alert(getBackendError(err, "Failed to create course"));
    }
  };

  const assignStaff = async () => {
    if (!selectedCourseId) {
      alert("Select a course first");
      return;
    }

    try {
      if (assign.instructorId) {
        await api.post(`/admin/courses/${selectedCourseId}/assign`, {
          user_id: assign.instructorId,
          role: "INSTRUCTOR",
        });
      }

      if (assign.courseLeadId) {
        await api.post(`/admin/courses/${selectedCourseId}/assign`, {
          user_id: assign.courseLeadId,
          role: "COURSE_LEAD",
        });
      }

      alert("Staff assigned successfully");
      await load();
    } catch (err) {
      console.error(err);
      alert(getBackendError(err, "Failed to assign staff"));
    }
  };

  const saveClos = async () => {
    if (!selectedCourseId) {
      alert("Select a course first");
      return;
    }

    const cleaned = clos
      .map((c) => ({
        code: (c.code || "").trim(),
        description: (c.description || "").trim(),
      }))
      .filter((c) => c.code && c.description);

    try {
      await api.put(`/admin/courses/${selectedCourseId}/clos`, {
        clos: cleaned,
      });

      alert("CLOs saved for this course");
      await load();
    } catch (err) {
      console.error(err);
      alert(getBackendError(err, "Failed to save CLOs"));
    }
  };

  const createTeacherUser = async (e) => {
    e.preventDefault();

    const username = normalizeUsername(newUser.username);
    const email = normalizeEmail(newUser.email);
    const department = normalizeDepartment(newUser.department);

    if (!username) {
      alert("Username is required");
      return;
    }

    if (!email) {
      alert("Email is required");
      return;
    }

    if (!isStrongPassword(newUser.password)) {
      alert(PASSWORD_HELP);
      return;
    }

    try {
      await api.post("/admin/users", {
        full_name: newUser.full_name.trim(),
        username,
        email,
        department: department || null,
        role: newUser.role,
        password: newUser.password,
      });

      alert("User created successfully");

      setNewUser({
        full_name: "",
        username: "",
        email: "",
        department: "CS",
        role: "instructor",
        password: "Teacher123",
      });

      setShowCreatePassword(false);

      await load();
    } catch (err) {
      console.error(err);
      alert(getBackendError(err, "Failed to create user"));
    }
  };

  const selectUserForEdit = (id) => {
    setEditUserId(id);
    setShowEditPassword(false);

    const u = allTeachers.find((x) => x.id === id);

    if (!u) {
      setEditForm({
        full_name: "",
        username: "",
        email: "",
        department: "CS",
        password: "",
      });
      return;
    }

    setEditForm({
      full_name: u.full_name || "",
      username: u.username || "",
      email: u.email || "",
      department: normalizeDepartment(u.department) || "CS",
      password: "",
    });
  };

  const saveEditedUser = async () => {
    if (!editUserId) {
      alert("Select a teacher first");
      return;
    }

    if (!editForm.full_name.trim()) {
      alert("Full name is required");
      return;
    }

    if (!editForm.username.trim()) {
      alert("Username is required");
      return;
    }

    if (!editForm.email.trim()) {
      alert("Email is required");
      return;
    }

    if (
      (editForm.password || "").trim() &&
      !isStrongPassword(editForm.password.trim())
    ) {
      alert(PASSWORD_HELP);
      return;
    }

    const payload = {
      full_name: editForm.full_name.trim(),
      username: normalizeUsername(editForm.username),
      email: normalizeEmail(editForm.email),
      department: normalizeDepartment(editForm.department) || null,
    };

    if ((editForm.password || "").trim()) {
      payload.password = editForm.password.trim();
    }

    setSavingEdit(true);

    try {
      await api.put(`/admin/users/${editUserId}`, payload);

      alert("User updated successfully");

      setEditUserId("");
      setEditForm({
        full_name: "",
        username: "",
        email: "",
        department: "CS",
        password: "",
      });

      setShowEditPassword(false);

      await load();
    } catch (err) {
      console.error(err);
      alert(getBackendError(err, "Failed to update user"));
    } finally {
      setSavingEdit(false);
    }
  };

  const deleteEditedUser = async () => {
    if (!editUserId) {
      alert("Select a teacher first");
      return;
    }

    const selectedUser = allTeachers.find((u) => u.id === editUserId);

    const confirmDelete = window.confirm(
      `Are you sure you want to delete ${
        selectedUser?.full_name || "this user"
      }? Their course assignments will also be removed.`
    );

    if (!confirmDelete) return;

    try {
      await api.delete(`/admin/users/${editUserId}`);

      alert("User deleted successfully");

      setEditUserId("");
      setEditForm({
        full_name: "",
        username: "",
        email: "",
        department: "CS",
        password: "",
      });

      setShowEditPassword(false);

      await load();
    } catch (err) {
      console.error(err);
      alert(getBackendError(err, "Failed to delete user"));
    }
  };

  const selectCourseForEdit = (id) => {
    setEditCourseId(id);

    const c = courses.find((x) => x.id === id);

    if (!c) {
      setEditCourseForm({
        course_code: "",
        course_name: "",
        semester: "",
        year: "",
        department: "CS",
        instructor: "",
        clos: "[]",
      });
      return;
    }

    setEditCourseForm({
      course_code: normalizeCourseCode(c.course_code || ""),
      course_name: c.course_name || "",
      semester: c.semester || "",
      year: c.year || "",
      department: normalizeDepartment(c.department) || "CS",
      instructor: c.instructor || "",
      clos: c.clos || "[]",
    });
  };

  const saveEditedCourse = async () => {
    if (!editCourseId) {
      alert("Select a course first");
      return;
    }

    if (!editCourseForm.course_code.trim()) {
      alert("Course code is required");
      return;
    }

    if (!editCourseForm.course_name.trim()) {
      alert("Course name is required");
      return;
    }

    if (!editCourseForm.department.trim()) {
      alert("Department is required");
      return;
    }

    const courseCode = normalizeCourseCode(editCourseForm.course_code);
    const department = normalizeDepartment(editCourseForm.department);

    if (!isValidCourseCodeForDepartment(courseCode, department)) {
      alert(COURSE_CODE_HELP);
      return;
    }

    const payload = {
      course_code: courseCode,
      course_name: editCourseForm.course_name.trim(),
      semester: editCourseForm.semester.trim(),
      year: editCourseForm.year.trim(),
      department,
      instructor: editCourseForm.instructor.trim(),
      clos: editCourseForm.clos || "[]",
    };

    setSavingCourseEdit(true);

    try {
      await api.put(`/admin/courses/${editCourseId}`, payload);

      alert("Course updated successfully");

      await load();
    } catch (err) {
      console.error(err);
      alert(getBackendError(err, "Failed to update course"));
    } finally {
      setSavingCourseEdit(false);
    }
  };

  const deleteEditedCourse = async () => {
    if (!editCourseId) {
      alert("Select a course first");
      return;
    }

    const selected = courses.find((c) => c.id === editCourseId);

    const confirmDelete = window.confirm(
      `Are you sure you want to delete ${
        selected?.course_code || "this course"
      }? Its staff assignments will also be removed.`
    );

    if (!confirmDelete) return;

    try {
      await api.delete(`/admin/courses/${editCourseId}`);

      alert("Course deleted successfully");

      setEditCourseId("");
      setEditCourseForm({
        course_code: "",
        course_name: "",
        semester: "",
        year: "",
        department: "CS",
        instructor: "",
        clos: "[]",
      });

      await load();
    } catch (err) {
      console.error(err);
      alert(getBackendError(err, "Failed to delete course"));
    }
  };

  const handleSelectedCourseChange = (courseId) => {
    setSelectedCourseId(courseId);
    setClosLoading(true);
    setClos([{ code: "CLO1", description: "" }]);
  };

  const createOffering = async (e) => {
    e.preventDefault();

    if (!offeringForm.course_id) {
      alert("Select a course");
      return;
    }

    if (!offeringForm.instructor_id) {
      alert("Select an instructor");
      return;
    }

    if (!offeringForm.semester.trim()) {
      alert("Semester is required");
      return;
    }

    if (!offeringForm.session.trim()) {
      alert("Session is required");
      return;
    }

    if (!offeringForm.batch.trim()) {
      alert("Batch is required");
      return;
    }

    if (!offeringForm.section.trim()) {
      alert("Section is required");
      return;
    }

    setSavingOffering(true);

    try {
      const selectedCourse = courses.find((c) => c.id === offeringForm.course_id);
      const selectedInstructor = allTeachers.find(
        (u) => u.id === offeringForm.instructor_id
      );

      const autoTitle =
        offeringForm.title?.trim() ||
        `${selectedCourse?.course_code || "Course"} - ${
          offeringForm.semester
        } ${offeringForm.session} - ${offeringForm.batch}${offeringForm.section}`;

      const payload = {
        course_id: offeringForm.course_id,
        instructor_id: offeringForm.instructor_id,
        course_lead_id: offeringForm.course_lead_id || null,
        semester: offeringForm.semester.trim(),
        session: offeringForm.session.trim(),
        batch: offeringForm.batch.trim(),
        section: offeringForm.section.trim(),
        department:
          normalizeDepartment(offeringForm.department) ||
          normalizeDepartment(selectedCourse?.department) ||
          null,
        title: autoTitle,
      };

      const res = await api.post("/course-offerings/", payload);

      alert(
        `Course offering created successfully${
          selectedInstructor?.full_name ? ` for ${selectedInstructor.full_name}` : ""
        }`
      );

      const newOffering = res.data;

      await loadOfferings();

      if (newOffering?.id) {
        setSelectedOfferingId(newOffering.id);
      }
    } catch (err) {
      console.error(err);
      alert(getBackendError(err, "Failed to create course offering"));
    } finally {
      setSavingOffering(false);
    }
  };

  const archiveOffering = async (offeringId) => {
    if (!offeringId) {
      alert("Select an offering first");
      return;
    }

    const selected = offerings.find((o) => o.id === offeringId);

    const ok = window.confirm(
      `Archive this offering?\n\n${selected ? formatOfferingOption(selected) : ""}`
    );

    if (!ok) return;

    try {
      await api.delete(`/course-offerings/${offeringId}`);

      alert("Course offering archived successfully");

      setSelectedOfferingId("");
      setOfferingStudents([]);

      await loadOfferings();
    } catch (err) {
      console.error(err);
      alert(getBackendError(err, "Failed to archive offering"));
    }
  };

  const bulkAddStudents = async () => {
    if (!selectedOfferingId) {
      alert("Select an offering first");
      return;
    }

    const students = parseStudentsText(studentsText);

    if (!students.length) {
      alert(
        "No valid students found. Use format: roll_no, student name, email optional"
      );
      return;
    }

    setSavingStudents(true);

    try {
      const res = await api.post(
        `/course-offerings/${selectedOfferingId}/students/bulk`,
        { students }
      );

      alert(
        `Students saved. Created: ${res?.data?.created ?? 0}, Updated: ${
          res?.data?.updated ?? 0
        }, Skipped: ${res?.data?.skipped ?? 0}`
      );

      await loadOfferingStudents(selectedOfferingId);
      await loadOfferings();
    } catch (err) {
      console.error(err);
      alert(getBackendError(err, "Failed to add students"));
    } finally {
      setSavingStudents(false);
    }
  };

  const removeOfferingStudent = async (studentRowId) => {
    if (!selectedOfferingId || !studentRowId) return;

    const ok = window.confirm("Remove this student from the offering?");
    if (!ok) return;

    try {
      await api.delete(
        `/course-offerings/${selectedOfferingId}/students/${studentRowId}`
      );

      await loadOfferingStudents(selectedOfferingId);
      await loadOfferings();
    } catch (err) {
      console.error(err);
      alert(getBackendError(err, "Failed to remove student"));
    }
  };

  const selectedCourse = courses.find((c) => c.id === selectedCourseId);
  const selectedOffering = offerings.find((o) => o.id === selectedOfferingId);

  return (
    <div style={styles.page}>
      <div style={styles.headerCard}>
        <div style={styles.titleRow}>
          <h2 style={styles.h2}>Admin Panel</h2>
          <span style={styles.chip}>Role: {user?.role || "admin"}</span>
        </div>

        <p style={styles.hint}>
          Create courses, create teacher users, edit/delete records, assign
          staff, set course-specific CLOs, and manage course offerings/classes.
        </p>

        {loading && (
          <div style={{ ...styles.small, marginTop: 8 }}>
            Loading admin data...
          </div>
        )}
      </div>

      {/* CREATE TEACHER USER */}
      <div style={styles.card}>
        <div style={styles.titleRow}>
          <h3 style={styles.h3}>Create Teacher User</h3>
          <span style={styles.chip}>Instructor / Course Lead</span>
        </div>

        <p style={styles.hint}>
          Create Instructor or Course Lead accounts. Username and email must be
          unique.
        </p>

        <form onSubmit={createTeacherUser} style={styles.grid}>
          <div style={styles.row}>
            <input
              style={styles.input}
              placeholder="Full Name"
              value={newUser.full_name}
              onChange={(e) =>
                setNewUser({ ...newUser, full_name: e.target.value })
              }
              required
            />

            <select
              style={{ ...styles.select, width: 220 }}
              value={newUser.role}
              onChange={(e) =>
                setNewUser({ ...newUser, role: e.target.value })
              }
            >
              <option value="instructor">Instructor</option>
              <option value="course_lead">Course Lead</option>
            </select>
          </div>

          <div style={styles.row}>
            <input
              style={styles.input}
              placeholder="Username"
              value={newUser.username}
              onChange={(e) =>
                setNewUser({ ...newUser, username: e.target.value })
              }
              required
            />

            <input
              style={styles.input}
              placeholder="Email"
              type="email"
              value={newUser.email}
              onChange={(e) =>
                setNewUser({ ...newUser, email: e.target.value })
              }
              required
            />
          </div>

          <div style={styles.row}>
            <select
              style={styles.select}
              value={newUser.department}
              onChange={(e) =>
                setNewUser({ ...newUser, department: e.target.value })
              }
            >
              {DEPARTMENTS.map((dept) => (
                <option key={dept} value={dept}>
                  {dept}
                </option>
              ))}
            </select>

            <div style={styles.passwordWrap}>
              <input
                style={styles.passwordInput}
                placeholder="Password"
                type={showCreatePassword ? "text" : "password"}
                value={newUser.password}
                onChange={(e) =>
                  setNewUser({ ...newUser, password: e.target.value })
                }
                required
              />

              <button
                type="button"
                style={styles.eyeBtn}
                onClick={() => setShowCreatePassword((prev) => !prev)}
                title={showCreatePassword ? "Hide password" : "Show password"}
              >
                {showCreatePassword ? "🙈" : "👁️"}
              </button>
            </div>
          </div>

          <div style={styles.small}>{PASSWORD_HELP}</div>

          <button style={styles.btnPrimary} type="submit">
            Create Teacher
          </button>
        </form>
      </div>

      {/* EDIT / DELETE TEACHER */}
      <div style={styles.card}>
        <div style={styles.titleRow}>
          <h3 style={styles.h3}>Edit / Delete Teacher or Course Lead</h3>
          <span style={styles.chip}>Admin Only</span>
        </div>

        <p style={styles.hint}>
          Update teacher details, reset password, or delete a non-admin account.
        </p>

        <div style={styles.grid}>
          <select
            style={styles.select}
            value={editUserId}
            onChange={(e) => selectUserForEdit(e.target.value)}
          >
            <option value="">Select Teacher…</option>
            {allTeachers.map((u) => (
              <option key={u.id} value={u.id}>
                {formatUserOption(u)}
              </option>
            ))}
          </select>

          <div style={styles.row}>
            <input
              style={styles.input}
              placeholder="Full Name"
              value={editForm.full_name}
              onChange={(e) =>
                setEditForm({ ...editForm, full_name: e.target.value })
              }
            />

            <input
              style={styles.input}
              placeholder="Username"
              value={editForm.username}
              onChange={(e) =>
                setEditForm({ ...editForm, username: e.target.value })
              }
            />
          </div>

          <div style={styles.row}>
            <input
              style={styles.input}
              placeholder="Email"
              type="email"
              value={editForm.email}
              onChange={(e) =>
                setEditForm({ ...editForm, email: e.target.value })
              }
            />

            <select
              style={styles.select}
              value={editForm.department}
              onChange={(e) =>
                setEditForm({ ...editForm, department: e.target.value })
              }
            >
              {DEPARTMENTS.map((dept) => (
                <option key={dept} value={dept}>
                  {dept}
                </option>
              ))}
            </select>
          </div>

          <div style={styles.passwordWrap}>
            <input
              style={styles.passwordInput}
              type={showEditPassword ? "text" : "password"}
              placeholder="New Password Optional"
              value={editForm.password}
              onChange={(e) =>
                setEditForm({ ...editForm, password: e.target.value })
              }
            />

            <button
              type="button"
              style={styles.eyeBtn}
              onClick={() => setShowEditPassword((prev) => !prev)}
              title={showEditPassword ? "Hide password" : "Show password"}
            >
              {showEditPassword ? "🙈" : "👁️"}
            </button>
          </div>

          <div style={styles.small}>
            Password reset is optional. If left empty, the old password remains
            unchanged. {PASSWORD_HELP}
          </div>

          <div style={styles.row}>
            <button
              style={styles.btnPrimary}
              onClick={saveEditedUser}
              type="button"
              disabled={savingEdit}
            >
              {savingEdit ? "Saving..." : "Save User Changes"}
            </button>

            <button
              style={styles.btnDanger}
              onClick={deleteEditedUser}
              type="button"
              disabled={savingEdit}
            >
              Delete User
            </button>

            <button
              style={styles.btn}
              type="button"
              onClick={() => {
                setEditUserId("");
                setEditForm({
                  full_name: "",
                  username: "",
                  email: "",
                  department: "CS",
                  password: "",
                });
                setShowEditPassword(false);
              }}
              disabled={savingEdit}
            >
              Clear
            </button>
          </div>
        </div>
      </div>

      {/* CREATE COURSE */}
      <div style={styles.card}>
        <h3 style={styles.h3}>Create Course</h3>

        <form onSubmit={createCourse} style={styles.grid}>
          <select
            style={styles.select}
            value={form.department}
            onChange={(e) =>
              setForm({
                ...form,
                department: e.target.value,
                course_code: `${e.target.value}-`,
              })
            }
            required
          >
            {DEPARTMENTS.map((dept) => (
              <option key={dept} value={dept}>
                {dept}
              </option>
            ))}
          </select>

          <input
            style={styles.input}
            placeholder="Course Code e.g. CS-401"
            value={form.course_code}
            onChange={(e) =>
              setForm({
                ...form,
                course_code: e.target.value.toUpperCase(),
              })
            }
            required
          />

          <div style={styles.small}>{COURSE_CODE_HELP}</div>

          <input
            style={styles.input}
            placeholder="Course Name"
            value={form.course_name}
            onChange={(e) => setForm({ ...form, course_name: e.target.value })}
            required
          />

          <div style={styles.row}>
            <input
              style={styles.input}
              placeholder="Semester"
              value={form.semester}
              onChange={(e) => setForm({ ...form, semester: e.target.value })}
            />

            <input
              style={styles.input}
              placeholder="Year"
              value={form.year}
              onChange={(e) => setForm({ ...form, year: e.target.value })}
            />
          </div>

          <button style={styles.btnPrimary} type="submit">
            Create Course
          </button>
        </form>
      </div>

      {/* EDIT / DELETE COURSE */}
      <div style={styles.card}>
        <div style={styles.titleRow}>
          <h3 style={styles.h3}>Edit / Delete Course</h3>
          <span style={styles.chip}>Course Management</span>
        </div>

        <p style={styles.hint}>
          Update course information or delete a course. Deleting a course will
          also remove staff assignments.
        </p>

        <div style={styles.grid}>
          <select
            style={styles.select}
            value={editCourseId}
            onChange={(e) => selectCourseForEdit(e.target.value)}
          >
            <option value="">Select Course…</option>
            {courses.map((c) => (
              <option key={c.id} value={c.id}>
                {formatCourseOption(c)}
              </option>
            ))}
          </select>

          <div style={styles.row}>
            <select
              style={styles.select}
              value={editCourseForm.department}
              onChange={(e) =>
                setEditCourseForm({
                  ...editCourseForm,
                  department: e.target.value,
                  course_code: `${e.target.value}-`,
                })
              }
            >
              {DEPARTMENTS.map((dept) => (
                <option key={dept} value={dept}>
                  {dept}
                </option>
              ))}
            </select>

            <input
              style={styles.input}
              placeholder="Course Code"
              value={editCourseForm.course_code}
              onChange={(e) =>
                setEditCourseForm({
                  ...editCourseForm,
                  course_code: e.target.value.toUpperCase(),
                })
              }
            />
          </div>

          <div style={styles.small}>{COURSE_CODE_HELP}</div>

          <input
            style={styles.input}
            placeholder="Course Name"
            value={editCourseForm.course_name}
            onChange={(e) =>
              setEditCourseForm({
                ...editCourseForm,
                course_name: e.target.value,
              })
            }
          />

          <div style={styles.row}>
            <input
              style={styles.input}
              placeholder="Semester"
              value={editCourseForm.semester}
              onChange={(e) =>
                setEditCourseForm({
                  ...editCourseForm,
                  semester: e.target.value,
                })
              }
            />

            <input
              style={styles.input}
              placeholder="Year"
              value={editCourseForm.year}
              onChange={(e) =>
                setEditCourseForm({
                  ...editCourseForm,
                  year: e.target.value,
                })
              }
            />
          </div>

          <input
            style={styles.input}
            placeholder="Instructor Display Name"
            value={editCourseForm.instructor}
            onChange={(e) =>
              setEditCourseForm({
                ...editCourseForm,
                instructor: e.target.value,
              })
            }
          />

          <textarea
            style={styles.textarea}
            placeholder='CLO JSON e.g. [{"code":"CLO1","description":"..."}]'
            value={editCourseForm.clos}
            onChange={(e) =>
              setEditCourseForm({
                ...editCourseForm,
                clos: e.target.value,
              })
            }
          />

          <div style={styles.row}>
            <button
              style={styles.btnPrimary}
              onClick={saveEditedCourse}
              type="button"
              disabled={savingCourseEdit}
            >
              {savingCourseEdit ? "Saving..." : "Save Course Changes"}
            </button>

            <button
              style={styles.btnDanger}
              onClick={deleteEditedCourse}
              type="button"
              disabled={savingCourseEdit}
            >
              Delete Course
            </button>

            <button
              style={styles.btn}
              type="button"
              onClick={() => {
                setEditCourseId("");
                setEditCourseForm({
                  course_code: "",
                  course_name: "",
                  semester: "",
                  year: "",
                  department: "CS",
                  instructor: "",
                  clos: "[]",
                });
              }}
              disabled={savingCourseEdit}
            >
              Clear
            </button>
          </div>
        </div>
      </div>

      {/* COURSE OFFERINGS */}
      <div style={styles.card}>
        <div style={styles.titleRow}>
          <h3 style={styles.h3}>Course Offerings / Class Sections</h3>
          <span style={styles.chip}>Phase 1: Batch + Section + Teacher</span>
        </div>

        <p style={styles.hint}>
          Create a class-specific offering of a course. This separates same
          courses taught by different teachers, batches, sections, and sessions.
        </p>

        <form onSubmit={createOffering} style={styles.grid}>
          <select
            style={styles.select}
            value={offeringForm.course_id}
            onChange={(e) => {
              const course = courses.find((c) => c.id === e.target.value);

              setOfferingForm({
                ...offeringForm,
                course_id: e.target.value,
                department: normalizeDepartment(course?.department) || "CS",
              });
            }}
            required
          >
            <option value="">Select Course…</option>
            {courses.map((c) => (
              <option key={c.id} value={c.id}>
                {formatCourseOption(c)}
              </option>
            ))}
          </select>

          <div style={styles.row}>
            <select
              style={styles.select}
              value={offeringForm.instructor_id}
              onChange={(e) =>
                setOfferingForm({
                  ...offeringForm,
                  instructor_id: e.target.value,
                })
              }
              required
            >
              <option value="">Select Instructor…</option>
              {instructors.map((u) => (
                <option key={u.id} value={u.id}>
                  {formatUserOption(u)}
                </option>
              ))}
            </select>

            <select
              style={styles.select}
              value={offeringForm.course_lead_id}
              onChange={(e) =>
                setOfferingForm({
                  ...offeringForm,
                  course_lead_id: e.target.value,
                })
              }
            >
              <option value="">Optional Course Lead…</option>
              {courseLeads.map((u) => (
                <option key={u.id} value={u.id}>
                  {formatUserOption(u)}
                </option>
              ))}
            </select>
          </div>

          <div style={styles.row}>
            <input
              style={styles.input}
              placeholder="Semester e.g. Spring"
              value={offeringForm.semester}
              onChange={(e) =>
                setOfferingForm({ ...offeringForm, semester: e.target.value })
              }
              required
            />

            <input
              style={styles.input}
              placeholder="Session e.g. 2026 or 2025-26"
              value={offeringForm.session}
              onChange={(e) =>
                setOfferingForm({ ...offeringForm, session: e.target.value })
              }
              required
            />
          </div>

          <div style={styles.row}>
            <input
              style={styles.input}
              placeholder="Batch e.g. BSCS 8"
              value={offeringForm.batch}
              onChange={(e) =>
                setOfferingForm({ ...offeringForm, batch: e.target.value })
              }
              required
            />

            <input
              style={styles.input}
              placeholder="Section e.g. A / B / C"
              value={offeringForm.section}
              onChange={(e) =>
                setOfferingForm({ ...offeringForm, section: e.target.value })
              }
              required
            />

            <select
              style={styles.select}
              value={offeringForm.department}
              onChange={(e) =>
                setOfferingForm({
                  ...offeringForm,
                  department: e.target.value,
                })
              }
            >
              {DEPARTMENTS.map((dept) => (
                <option key={dept} value={dept}>
                  {dept}
                </option>
              ))}
            </select>
          </div>

          <input
            style={styles.input}
            placeholder="Optional title e.g. CS-401 Data Warehousing - Spring 2026 - BSCS 8B"
            value={offeringForm.title}
            onChange={(e) =>
              setOfferingForm({ ...offeringForm, title: e.target.value })
            }
          />

          <div style={styles.row}>
            <button
              style={styles.btnPrimary}
              type="submit"
              disabled={savingOffering}
            >
              {savingOffering ? "Creating..." : "Create Course Offering"}
            </button>

            <button
              style={styles.btn}
              type="button"
              onClick={() => loadOfferings().catch(console.error)}
              disabled={offeringLoading}
            >
              {offeringLoading ? "Refreshing..." : "Refresh Offerings"}
            </button>
          </div>
        </form>

        <div style={styles.divider} />

        <div style={styles.grid}>
          <div style={styles.titleRow}>
            <h4 style={styles.h4}>Existing Active Offerings</h4>
            <span style={styles.chip}>{offerings.length} offerings</span>
          </div>

          <select
            style={styles.select}
            value={selectedOfferingId}
            onChange={(e) => {
              setSelectedOfferingId(e.target.value);
              setOfferingStudents([]);
            }}
          >
            <option value="">Select offering…</option>
            {offerings.map((o) => (
              <option key={o.id} value={o.id}>
                {formatOfferingOption(o)}
              </option>
            ))}
          </select>

          {selectedOffering ? (
            <div style={styles.miniCard}>
              <div style={styles.titleRow}>
                <div>
                  <b>{selectedOffering.title || selectedOffering.course_code}</b>
                  <div style={{ ...styles.small, marginTop: 4 }}>
                    {selectedOffering.course_code} —{" "}
                    {selectedOffering.course_name || "-"} |{" "}
                    {selectedOffering.semester} {selectedOffering.session} |{" "}
                    {selectedOffering.batch} {selectedOffering.section}
                  </div>
                  <div style={{ ...styles.small, marginTop: 4 }}>
                    Instructor:{" "}
                    <b>{selectedOffering.instructor_name || "Not set"}</b>
                    {" · "}
                    Course Lead:{" "}
                    <b>{selectedOffering.course_lead_name || "Not set"}</b>
                    {" · "}
                    Students: <b>{selectedOffering.students_count || 0}</b>
                  </div>
                </div>

                <button
                  style={styles.btnDanger}
                  type="button"
                  onClick={() => archiveOffering(selectedOffering.id)}
                >
                  Archive Offering
                </button>
              </div>
            </div>
          ) : (
            <div style={styles.small}>
              No offering selected. Create or select an offering to manage its
              students.
            </div>
          )}
        </div>
      </div>

      {/* OFFERING STUDENTS */}
      <div style={styles.card}>
        <div style={styles.titleRow}>
          <h3 style={styles.h3}>Offering Students</h3>
          <span style={styles.chip}>Student Enrollment</span>
        </div>

        <p style={styles.hint}>
          Add students to the selected course offering. This will later allow
          student-wise result tracking, missed assessments, and CLO attainment.
        </p>

        <div style={styles.grid}>
          <select
            style={styles.select}
            value={selectedOfferingId}
            onChange={(e) => {
              setSelectedOfferingId(e.target.value);
              setOfferingStudents([]);
            }}
          >
            <option value="">Select offering…</option>
            {offerings.map((o) => (
              <option key={o.id} value={o.id}>
                {formatOfferingOption(o)}
              </option>
            ))}
          </select>

          <textarea
            style={{ ...styles.textarea, minHeight: 120 }}
            placeholder={`Paste students like:\n221001, Ali Khan, ali@example.com\n221002, Sara Ahmed, sara@example.com\n\nOr JSON array with roll_no, student_name, email`}
            value={studentsText}
            onChange={(e) => setStudentsText(e.target.value)}
          />

          <div style={styles.small}>
            Format: <b>roll_no, student_name, email optional</b>. Example:
            <br />
            221001, Ali Khan, ali@example.com
          </div>

          <div style={styles.row}>
            <button
              style={styles.btnPrimary}
              type="button"
              onClick={bulkAddStudents}
              disabled={savingStudents || !selectedOfferingId}
            >
              {savingStudents ? "Saving Students..." : "Bulk Add / Update Students"}
            </button>

            <button
              style={styles.btn}
              type="button"
              onClick={() => loadOfferingStudents(selectedOfferingId)}
              disabled={!selectedOfferingId || studentsLoading}
            >
              {studentsLoading ? "Loading..." : "Refresh Students"}
            </button>
          </div>

          <div style={styles.tableWrap}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Roll No</th>
                  <th style={styles.th}>Student Name</th>
                  <th style={styles.th}>Email</th>
                  <th style={styles.th}>Status</th>
                  <th style={styles.th}>Action</th>
                </tr>
              </thead>

              <tbody>
                {offeringStudents.map((s) => (
                  <tr key={s.id}>
                    <td style={{ ...styles.td, fontWeight: 900 }}>
                      {s.roll_no}
                    </td>
                    <td style={styles.td}>{s.student_name}</td>
                    <td style={styles.td}>{s.email || "-"}</td>
                    <td style={styles.td}>
                      <span style={styles.chip}>{s.status || "active"}</span>
                    </td>
                    <td style={styles.td}>
                      <button
                        style={styles.btn}
                        type="button"
                        onClick={() => removeOfferingStudent(s.id)}
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}

                {!offeringStudents.length && (
                  <tr>
                    <td colSpan={5} style={styles.td}>
                      {studentsLoading
                        ? "Loading students..."
                        : "No students added to this offering yet."}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* ASSIGN STAFF */}
      <div style={styles.card}>
        <h3 style={styles.h3}>Assign Staff to Course</h3>

        <p style={styles.hint}>
          This is the old course-level assignment. The new offering system above
          is more accurate for semester, batch, and section-wise teaching.
        </p>

        <div style={styles.grid}>
          <select
            style={styles.select}
            value={selectedCourseId}
            onChange={(e) => handleSelectedCourseChange(e.target.value)}
          >
            <option value="">Select course…</option>
            {courses.map((c) => (
              <option key={c.id} value={c.id}>
                {formatCourseOption(c)}
              </option>
            ))}
          </select>

          <div style={styles.row}>
            <select
              style={styles.select}
              value={assign.instructorId}
              onChange={(e) =>
                setAssign({ ...assign, instructorId: e.target.value })
              }
            >
              <option value="">Assign Instructor…</option>
              {instructors.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.full_name} ({u.username})
                </option>
              ))}
            </select>

            <select
              style={styles.select}
              value={assign.courseLeadId}
              onChange={(e) =>
                setAssign({ ...assign, courseLeadId: e.target.value })
              }
            >
              <option value="">Assign Course Lead…</option>
              {courseLeads.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.full_name} ({u.username})
                </option>
              ))}
            </select>
          </div>

          <button style={styles.btnPrimary} onClick={assignStaff} type="button">
            Save Assignments
          </button>

          {selectedCourse ? (
            <div style={styles.small}>
              Selected: <b>{selectedCourse.course_code}</b> —{" "}
              {selectedCourse.course_name}
            </div>
          ) : null}
        </div>
      </div>

      {/* SET CLOS */}
      <div style={styles.card}>
        <div style={styles.titleRow}>
          <h3 style={styles.h3}>Set CLOs Course-Specific</h3>
          <span style={styles.chip}>Single Source: courses.clos</span>
        </div>

        <p style={styles.hint}>
          Select a course, then add CLOs. These are stored in courses.clos as
          JSON and used by course guide extraction, CLO alignment, grading, and
          reports.
        </p>

        <select
          style={styles.select}
          value={selectedCourseId}
          onChange={(e) => handleSelectedCourseChange(e.target.value)}
        >
          <option value="">Select course…</option>
          {courses.map((c) => (
            <option key={c.id} value={c.id}>
              {formatCourseOption(c)}
            </option>
          ))}
        </select>

        <div style={styles.divider} />

        {closLoading ? (
          <div style={styles.small}>Loading selected course CLOs...</div>
        ) : (
          <>
            {clos.map((c, idx) => (
              <div key={idx} style={{ ...styles.row, marginBottom: 8 }}>
                <input
                  style={{ ...styles.input, width: 130 }}
                  placeholder="CLO1"
                  value={c.code}
                  onChange={(e) => {
                    const next = [...clos];
                    next[idx] = { ...next[idx], code: e.target.value };
                    setClos(next);
                  }}
                />

                <input
                  style={styles.input}
                  placeholder="Description"
                  value={c.description}
                  onChange={(e) => {
                    const next = [...clos];
                    next[idx] = {
                      ...next[idx],
                      description: e.target.value,
                    };
                    setClos(next);
                  }}
                />

                <button
                  style={styles.btn}
                  onClick={() => setClos(clos.filter((_, i) => i !== idx))}
                  type="button"
                  title="Remove"
                >
                  ✕
                </button>
              </div>
            ))}

            <div style={styles.row}>
              <button
                style={styles.btn}
                type="button"
                onClick={() =>
                  setClos([
                    ...clos,
                    {
                      code: `CLO${clos.length + 1}`,
                      description: "",
                    },
                  ])
                }
              >
                + Add CLO
              </button>

              <button style={styles.btnPrimary} type="button" onClick={saveClos}>
                Save CLOs
              </button>
            </div>
          </>
        )}

        <div style={{ ...styles.small, marginTop: 10 }}>
          Tip: Keep CLO code like <b>CLO1</b>, <b>CLO2</b>, and write meaningful
          descriptions.
        </div>
      </div>
    </div>
  );
}